/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { useMemo } from 'react';
import { Product } from '~/lib/data-contract/salesforce';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils/frame';
import useBasket from './useBasket';
import useCustomer from './useCustomer';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';

/**
 * Joins basket and customer hooks into a single hook for initializing their states
 * when the app loads on the client-side. Should only be use at top-level of app.
 * @returns {Object} - customer and basket objects
 */
const useProducts = (opts: { currency?: string } = {}) => {
    const { currency } = opts;
    const {
        data: { market },
    } = useFrame();
    const customer = useCustomer();
    const basket = useBasket({ currency });
    const { membership } = useLocalBasketState();
    const { convertPriceToLocale } = useUtils();

    const handlers = useMemo(
        () => ({
            getProducts: () => {
                /**
                 * TEMP: We'll remove this type guard once we have identified the correct typings for _productItemsDetail.
                 *
                 * A product collection is an object, is not null, is not an array, has keys, and
                 * its entries should have keys and an id property.
                 */
                const isProductCollection = (value: unknown): value is Record<string, Product> =>
                    typeof value === 'object' &&
                    value !== null &&
                    !Array.isArray(value) &&
                    Object.keys(value).length > 0 &&
                    Object.entries(value).every(
                        ([_key, product]) => Object.keys(product).length && product.id,
                    );

                if (
                    !customer.customerId ||
                    !basket?.basketId ||
                    !(basket.itemCount() > 0) ||
                    // TODO(IMC-514): Add _productItemsDetail to Basket type to remove below comment.
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    !isProductCollection(basket?._productItemsDetail)
                ) {
                    return [];
                }

                // TODO(IMC-514): Add _productItemsDetail to Basket type to remove below comment.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const productItemsDetail = basket?._productItemsDetail as Record<string, Product>;

                return basket?.productItems?.map((item) => {
                    const itemId = item.productId;

                    const index = Object.entries(productItemsDetail).findIndex(
                        ([_key, value]) => value.id === itemId,
                    );

                    const productDetails = itemId ? productItemsDetail[itemId] : undefined;

                    return {
                        index,
                        ...item,
                        ...productDetails,
                    };
                }) as Product[]; // TODO(IMC-514): Remove assertion once we have the correct type for _productItemsDetail.
            },
            getProductsByShipment: (shipmentId: string) =>
                handlers.getProducts()?.filter((product) => product.shipmentId === shipmentId),
            getProductsQuantity: () => {
                return basket.itemAccumulatedCount();
            },
            getTotalPrice: () => {
                return convertPriceToLocale(basket.productTotal);
            },
            getPrices: () => {
                const discountPrice = basket.c_totalDiscountExclShipping ?? 0;
                const memberDiscount = basket.c_totalMemberDiscount ?? 0;
                const totalPrice = basket.c_subTotalRetailPrices ?? 0;
                const memberPrice = basket?.orderTotal && basket?.orderTotal - memberDiscount;
                const customerPrice = membership ? memberPrice : basket?.orderTotal;
                const priceMemberSelected = discountPrice + memberDiscount ?? 0;

                return {
                    discountPrice,
                    memberDiscount,
                    totalPrice,
                    memberPrice,
                    customerPrice: convertPriceToLocale(customerPrice),
                    priceMemberSelected,
                };
            },

            getHasFreeShipping: () => {
                if (
                    market?.minimumBasketTotalForFreeShipping &&
                    basket.c_freeShippingAllowed &&
                    basket.productSubTotal
                ) {
                    return basket.productSubTotal >= market.minimumBasketTotalForFreeShipping;
                }

                return false;
            },
            getAmountToFreeShipping: () => {
                if (
                    market?.minimumBasketTotalForFreeShipping &&
                    basket.c_freeShippingAllowed &&
                    basket.productSubTotal
                ) {
                    const result =
                        market.minimumBasketTotalForFreeShipping - basket.productSubTotal;
                    return convertPriceToLocale(result <= 0 ? 0 : result);
                }
            },
        }),
        [customer, basket],
    );

    return { handlers };
};

export default useProducts;
